import React from "react";
import { NavLink, Outlet, useNavigate, useLocation, Navigate } from "react-router-dom";
import {
  LayoutDashboard, FileText, MessageSquare, MessagesSquare,
  BarChart2, Bell, Settings, LogOut, Loader2,
} from "lucide-react";
import { Toaster } from "sonner";
import { adminAuth, adminLogout } from "@/lib/adminAuth";
import { AdminProvider, useAdmin } from "@/context/AdminContext";

const PAGE_TITLES = {
  "/admin": "Tableau de bord",
  "/admin/devis": "Devis",
  "/admin/contacts": "Contacts",
  "/admin/chatbot": "Chatbot Alex",
  "/admin/analytics": "Analytiques",
  "/admin/notifications": "Notifications",
  "/admin/settings": "Paramètres",
};

const Sidebar = () => {
  const nav = useNavigate();
  const { stats, unreadCount } = useAdmin();
  const user = adminAuth.getUser();

  const linkCls = ({ isActive }) =>
    `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      isActive
        ? "bg-white/20 text-white"
        : "text-white/70 hover:bg-white/10 hover:text-white"
    }`;

  const NavItem = ({ to, end, icon: Icon, label, badge }) => {
    const count =
      badge === "quotes24h" ? stats?.quotes_24h :
      badge === "contacts24h" ? stats?.contacts_24h : null;
    return (
      <NavLink to={to} end={end} className={linkCls} data-testid={`nav-${label.toLowerCase().replace(/\s/g, "-")}`}>
        <Icon className="h-4 w-4 flex-shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        {count > 0 && (
          <span className="bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 min-w-[18px] text-center">
            {count}
          </span>
        )}
      </NavLink>
    );
  };

  return (
    <aside className="w-[200px] flex-shrink-0 bg-brand-green flex flex-col h-screen sticky top-0 z-10">
      <div className="px-4 py-5 border-b border-white/10">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
            <span className="text-brand-green font-extrabold text-sm">B</span>
          </div>
          <div>
            <p className="text-white font-extrabold text-sm leading-none">BIONET</p>
            <p className="text-white/60 text-[10px] mt-0.5">Administration</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 p-2 flex flex-col gap-0.5 overflow-y-auto">
        <NavItem to="/admin" end icon={LayoutDashboard} label="Tableau de bord" />
        <NavItem to="/admin/devis" icon={FileText} label="Devis" badge="quotes24h" />
        <NavItem to="/admin/contacts" icon={MessageSquare} label="Contacts" badge="contacts24h" />
        <NavItem to="/admin/chatbot" icon={MessagesSquare} label="Chatbot Alex" />
        <NavItem to="/admin/analytics" icon={BarChart2} label="Analytiques" />

        <div className="my-2 border-t border-white/10" />

        <NavLink to="/admin/notifications" className={linkCls} data-testid="nav-notifications">
          <Bell className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">Notifications</span>
          {unreadCount > 0 && (
            <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
          )}
        </NavLink>
        <NavItem to="/admin/settings" icon={Settings} label="Paramètres" />
      </nav>

      <div className="p-3 border-t border-white/10 flex items-center gap-2">
        <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
          {(user?.email?.[0] || "A").toUpperCase()}
        </div>
        <p className="text-white/70 text-xs truncate flex-1">{user?.email}</p>
        <button
          onClick={() => { adminLogout(); nav("/admin/login"); }}
          className="text-white/50 hover:text-white transition-colors"
          data-testid="admin-logout-button"
        >
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </aside>
  );
};

const AdminLayoutInner = () => {
  const { loading } = useAdmin();
  const { pathname } = useLocation();
  const title =
    PAGE_TITLES[pathname] ||
    (pathname.includes("/devis/") ? "Détail devis" :
     pathname.includes("/contacts/") ? "Détail contact" : "Admin");

  return (
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-brand-blue">Admin</p>
            <h1 className="font-heading font-extrabold text-xl text-slate-900 mt-0.5">{title}</h1>
          </div>
          <p className="text-xs text-slate-400">
            {new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
          </p>
        </header>
        <main className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-brand-green" />
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
};

const AdminLayout = () => {
  if (!adminAuth.getToken()) return <Navigate to="/admin/login" replace />;
  return (
    <AdminProvider>
      <Toaster position="top-right" richColors />
      <AdminLayoutInner />
    </AdminProvider>
  );
};

export default AdminLayout;
